import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import * as Papa from 'papaparse';
import { DynamicReviewsExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';

const MAX_CSV_FILE_SIZE = DynamicReviewsExtension.CSV_MAX_FILE_SIZE;
const REQUIRED_FIELDS = DynamicReviewsExtension.REQUIRED_FIELDS;
const CSV_TEMPLATE_FILENAME = 'pdq-product-reviews-template.csv';
const BYTES_PER_UNIT = 1024;
const FILE_SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB'];

const CSV_ERROR_MESSAGES = {
  NOT_CSV: 'Please upload a CSV file',
  FILE_TOO_LARGE: 'File size exceeds 5MB limit',
  READ_FAILED: 'Failed to read file',
  MISSING_DATA_ROW: 'CSV must contain headers and at least one data row',
  DUPLICATE_PRODUCT_IDS: 'Your CSV must contain only 1 review per product ID. Duplicates found',
} as const;

/**
 * Review CSV upload — template download, dropzone, validation and file toolbar — shared by Dynamic
 * Reviews and the checkout upsell.
 *
 * Expects a form group holding `csvFileUrl`, `csvFileName`, `csvFileSize`, `reviewCount` and
 * `pendingCsvContent`. The file is read in the browser into `pendingCsvContent`; the backend does
 * the S3 upload when the extension is saved.
 */
@Component({
  selector: 'pdq-review-csv-upload',
  templateUrl: './review-csv-upload.component.html',
  styleUrls: ['./review-csv-upload.component.scss'],
  standalone: false,
})
export class ReviewCsvUploadComponent {
  @Input() formGroup: FormGroup;

  uploadError: string | null = null;
  isDragOver = false;

  get hasFile(): boolean {
    return !!this.formGroup.get('csvFileUrl')?.value || !!this.formGroup.get('pendingCsvContent')?.value;
  }

  get fileName(): string {
    return this.formGroup.get('csvFileName')?.value || '';
  }

  get fileSize(): number {
    return this.formGroup.get('csvFileSize')?.value || 0;
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const unitIndex = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT));
    return `${parseFloat((bytes / Math.pow(BYTES_PER_UNIT, unitIndex)).toFixed(2))} ${FILE_SIZE_UNITS[unitIndex]}`;
  }

  downloadTemplate(): void {
    const headers = DynamicReviewsExtension.ALL_FIELDS.join(',');
    const exampleRows = [
      '1001,Classic White T-Shirt,4.5,"Love the fit and quality!",Sarah M.,4.7,128',
      '1002,Running Shoes Pro,3.8,"Very comfortable for long runs, even on rough terrain",James K.,4.2,256',
      '1003,Organic Coffee Beans,4.2,"Best coffee I have ever tried",Emily R.,4.8,89',
    ];

    this.downloadBlob(headers + '\n' + exampleRows.join('\n') + '\n', CSV_TEMPLATE_FILENAME);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const file = event.dataTransfer?.files?.[0];
    if (file) this.handleFileSelect(file);
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.handleFileSelect(file);
  }

  replaceFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (event) => this.onFileSelected(event);
    input.click();
  }

  retryUpload(): void {
    this.uploadError = null;
    this.replaceFile();
  }

  removeFile(): void {
    this.formGroup.patchValue({
      csvFileUrl: '',
      csvFileName: '',
      csvFileSize: 0,
      reviewCount: 0,
      pendingCsvContent: '',
    });
    this.uploadError = null;
    this.formGroup.markAsDirty();
  }

  downloadCurrentFile(): void {
    const pendingContent = this.formGroup.get('pendingCsvContent')?.value;
    const csvFileUrl = this.formGroup.get('csvFileUrl')?.value;
    const fileName = this.fileName || 'reviews.csv';

    if (pendingContent) {
      this.downloadBlob(pendingContent, fileName);
      return;
    }

    if (csvFileUrl) {
      const link = document.createElement('a');
      link.href = csvFileUrl;
      link.download = fileName;
      link.click();
    }
  }

  private handleFileSelect(file: File): void {
    this.uploadError = null;

    if (!file.name.endsWith('.csv')) {
      this.uploadError = CSV_ERROR_MESSAGES.NOT_CSV;
      return;
    }

    if (file.size > MAX_CSV_FILE_SIZE) {
      this.uploadError = CSV_ERROR_MESSAGES.FILE_TOO_LARGE;
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const csvContent = reader.result as string;
      if (!this.validateCsvHeaders(csvContent)) return;

      this.formGroup.patchValue({
        pendingCsvContent: csvContent,
        csvFileName: file.name,
        csvFileSize: file.size,
        reviewCount: this.countReviewsInCsv(csvContent),
      });
      this.formGroup.markAsDirty();
    };

    reader.onerror = () => {
      this.uploadError = CSV_ERROR_MESSAGES.READ_FAILED;
    };

    reader.readAsText(file);
  }

  private validateCsvHeaders(csvContent: string): boolean {
    const parsed = Papa.parse<Record<string, string>>(csvContent.trim(), {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.toLowerCase().trim(),
    });

    if (parsed.data.length === 0) {
      this.uploadError = CSV_ERROR_MESSAGES.MISSING_DATA_ROW;
      return false;
    }

    const headers = parsed.meta.fields ?? [];
    const missingHeaders = REQUIRED_FIELDS.filter((required) => !headers.includes(required));

    if (missingHeaders.length > 0) {
      this.uploadError = `Missing required columns: ${missingHeaders.join(', ')}`;
      return false;
    }

    const seenProductIds = new Set<string>();
    for (const row of parsed.data) {
      const productId = row['product_id']?.trim();
      if (!productId) continue;

      if (seenProductIds.has(productId)) {
        this.uploadError = `${CSV_ERROR_MESSAGES.DUPLICATE_PRODUCT_IDS}: ${productId}`;
        return false;
      }
      seenProductIds.add(productId);
    }

    return true;
  }

  private countReviewsInCsv(csvContent: string): number {
    return Papa.parse(csvContent.trim(), { header: true, skipEmptyLines: true }).data.length;
  }

  private downloadBlob(content: string, fileName: string): void {
    const url = window.URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
